import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { MENU_STRUCTURE } from '../lib/cms';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  
  // Mobile accordion expanded states
  const [mobileServicesOpen, setMobileServicesOpen] = useState(true);
  const [mobileExpandedGroup, setMobileExpandedGroup] = useState({});

  const dropdownRef = useRef(null);
  const dropdownTimeoutRef = useRef(null);
  const location = useLocation();

  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 200);
  };

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
  }, [location]);

  // Handle outside click for dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if current route is anywhere under /ydelser
  const isServicesPageActive = location.pathname.startsWith('/ydelser');

  const toggleMobileGroup = (groupTitle) => {
    setMobileExpandedGroup((prev) => ({
      ...prev,
      [groupTitle]: !prev[groupTitle]
    }));
  };

  const servicesMenuConfig = MENU_STRUCTURE.find((m) => m.isDropdown);

  return (
    <header className="sticky top-0 z-50 bg-[#141f14] text-white shadow-md border-b border-[#233323]">
      {/* Top info bar */}
      <div className="bg-[#0c140c] text-[#e8eee6] text-xs py-2 border-b border-[#233323]">
        <div className="max-w-[109rem] mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center space-x-6 text-[#cbd3c5]">
            <span className="flex items-center gap-2">
              <i className="fa-solid fa-location-dot text-[#9ecc49]" aria-hidden="true" />
              <span>Ishøj Bygade 90, 2635 Ishøj</span>
            </span>
            <span className="hidden sm:flex items-center gap-2">
              <i className="fa-solid fa-certificate text-[#9ecc49]" aria-hidden="true" />
              <span>Autoriseret Kloakmester • CVR: 43852507</span>
            </span>
          </div>

          <div className="flex items-center space-x-6">
            <a
              href="mailto:info@prentreprise.dk"
              className="hover:text-[#9ecc49] transition-colors whitespace-nowrap flex items-center gap-2"
            >
              <i className="fa-regular fa-envelope text-[#9ecc49]" aria-hidden="true" />
              <span className="whitespace-nowrap">info@prentreprise.dk</span>
            </a>
            <a
              href="tel:22231010"
              className="hover:text-[#9ecc49] transition-colors font-semibold text-white whitespace-nowrap flex items-center gap-2"
            >
              <i className="fa-solid fa-phone text-[#9ecc49]" aria-hidden="true" />
              <span className="whitespace-nowrap">Tlf. 22 23 10 10</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation container */}
      <div className="bg-white text-[#1f241d] border-b border-[#cbd3c5]">
        <div className="max-w-[109rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-2.5">
            
            {/* Logo, Navigation & CTA on one horizontal line */}
            <div className="flex items-center justify-between gap-4 lg:gap-8">
              {/* Left: Existing Logo */}
              <Link to="/" className="flex items-center py-0.5 shrink-0">
                <img
                  src="https://assets.auxodev.dk/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/imgi-1-pr-logo-1024x369.png"
                  alt="PR Entreprise & Anlæg - Autoriseret Kloakmester"
                  className="h-14 sm:h-16 lg:h-20 w-auto object-contain"
                />
              </Link>

              {/* Middle/Right: Desktop Main Navigation */}
              <div className="hidden min-[1200px]:flex items-center justify-end flex-1 px-4">
                <nav className="flex items-center justify-center space-x-1 font-semibold text-sm">
                
                {/* 1. Forside */}
                <NavLink
                  to="/"
                  end
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-none transition-colors ${
                      isActive ? 'bg-[#3d5229] text-white font-bold' : 'text-[#1f241d] hover:text-[#3d5229] hover:bg-[#dce2d7]/50'
                    }`
                  }
                >
                  Forside
                </NavLink>

                {/* 2. Vi tilbyder (Dropdown with multi-level tree) */}
                <div
                  className="relative"
                  ref={dropdownRef}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <NavLink
                    to="/ydelser"
                    className={`px-4 py-2 rounded-none transition-colors flex items-center gap-1.5 ${
                      isServicesPageActive && location.pathname !== '/ydelser/autoriseret-kloakmester'
                        ? 'bg-[#3d5229] text-white font-bold'
                        : 'text-[#1f241d] hover:text-[#3d5229] hover:bg-[#dce2d7]/50'
                    }`}
                    aria-haspopup="true"
                    aria-expanded={dropdownOpen}
                  >
                    <span>Vi tilbyder</span>
                    <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${dropdownOpen ? 'rotate-180 text-[#9ecc49]' : ''}`} aria-hidden="true" />
                  </NavLink>

                  {dropdownOpen && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-1.5 w-[750px] z-50 animate-slide-down">
                      <div className="bg-[#141f14] border border-[#233323] rounded-none shadow-2xl p-6 text-white grid grid-cols-3 gap-6">
                        
                        {servicesMenuConfig?.children.map((group) => {
                          const hasGroupChildren = Array.isArray(group.children) && group.children.length > 0;
                          return (
                            <div key={group.path} className="space-y-2">
                              {/* Parent Category Header */}
                              <Link
                                to={group.path}
                                className="font-heading font-extrabold text-sm text-[#9ecc49] hover:text-white transition-colors flex items-center justify-between border-b border-[#233323] pb-1.5 group"
                              >
                                <span>{group.title}</span>
                                {hasGroupChildren && (
                                  <i className="fa-solid fa-angle-right text-xs opacity-70 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
                                )}
                              </Link>

                              {/* Sub-level items */}
                              {hasGroupChildren && (
                                <ul className="space-y-1 pl-1">
                                  {group.children.map((subItem) => (
                                    <li key={subItem.path}>
                                      <Link
                                        to={subItem.path}
                                        className="text-xs text-gray-300 hover:text-[#9ecc49] hover:bg-[#233323] px-2 py-1 rounded-none block transition-colors"
                                      >
                                        
                                        <span>{subItem.title}</span>
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          );
                        })}

                        {/* Bottom Full Overview Link */}
                        <div className="col-span-3 pt-3 border-t border-[#233323] flex items-center justify-between text-xs">
                          <span className="text-gray-400">Har du en særlig opgave? Vi udfører skræddersyede entrepriser.</span>
                          <Link
                            to="/ydelser"
                            className="font-bold text-[#9ecc49] hover:underline flex items-center gap-1.5"
                          >
                            <span>Se alle ydelser oversigt</span>
                            <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                          </Link>
                        </div>

                      </div>
                    </div>
                  )}
                </div>

                {/* 3. Autoriseret kloakmester */}
                <NavLink
                  to="/ydelser/autoriseret-kloakmester"
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-none transition-colors ${
                      isActive ? 'bg-[#3d5229] text-white font-bold' : 'text-[#1f241d] hover:text-[#3d5229] hover:bg-[#dce2d7]/50'
                    }`
                  }
                >
                  Autoriseret kloakmester
                </NavLink>

                {/* 4. Om os */}
                <NavLink
                  to="/om-os"
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-none transition-colors ${
                      isActive ? 'bg-[#3d5229] text-white font-bold' : 'text-[#1f241d] hover:text-[#3d5229] hover:bg-[#dce2d7]/50'
                    }`
                  }
                >
                  Om os
                </NavLink>

                {/* 5. Kontakt */}
                <NavLink
                  to="/kontakt"
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-none transition-colors ${
                      isActive ? 'bg-[#3d5229] text-white font-bold' : 'text-[#1f241d] hover:text-[#3d5229] hover:bg-[#dce2d7]/50'
                    }`
                  }
                >
                  Kontakt
                </NavLink>

              </nav>
            </div>

            {/* Far Right: Desktop Primary CTA Button */}
            <div className="hidden min-[1200px]:flex items-center shrink-0">
              <Link
                to="/kontakt"
                className="bg-[#3d5229] hover:bg-[#2e3f1e] text-white font-bold text-sm px-5 py-2.5 rounded-none transition-colors duration-150 inline-flex items-center gap-2 shadow-sm whitespace-nowrap"
              >
                <span>Få et godt tilbud</span>
                <i className="fa-solid fa-arrow-right text-xs text-[#9ecc49]" aria-hidden="true" />
              </Link>
            </div>

            {/* Burger Menu Button - Visible ONLY under 1200px */}
            <div className="flex min-[1200px]:hidden items-center gap-2">
              <a
                href="tel:22231010"
                className="bg-[#9ecc49] text-[#1f241d] p-2.5 rounded-none text-sm font-bold flex items-center gap-1.5 whitespace-nowrap"
                aria-label="Ring op"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span className="hidden sm:inline text-xs whitespace-nowrap">22 23 10 10</span>
              </a>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2.5 rounded-none bg-[#3d5229] text-white hover:bg-[#2e3f1e] focus:outline-none cursor-pointer"
                aria-controls="mobile-menu"
                aria-expanded={mobileMenuOpen}
                aria-label="Åbn menu"
              >
                <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl w-6 text-center`} aria-hidden="true" />
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>

      {/* Mobile Navigation Panel - Visible ONLY when burger menu opened under 1200px */}
      {mobileMenuOpen && (
        <div
          className="min-[1200px]:hidden bg-[#141f14] border-t border-[#233323] px-4 pt-4 pb-6 space-y-2 animate-fade-in-down max-h-[calc(100dvh-100px)] overflow-y-auto"
          id="mobile-menu"
        >
          {/* Forside */}
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `block px-4 py-3 rounded-none text-base font-semibold ${
                isActive ? 'bg-[#3d5229] text-[#9ecc49]' : 'text-gray-200 hover:bg-[#233323]'
              }`
            }
          >
            Forside
          </NavLink>

          {/* Vi tilbyder Accordion Menu */}
          <div className="bg-[#0c140c] rounded-none border border-[#233323] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-[#182418] border-b border-[#233323]">
              <Link
                to="/ydelser"
                className={`text-base font-bold flex items-center gap-2 ${
                  isServicesPageActive && location.pathname !== '/ydelser/autoriseret-kloakmester' ? 'text-[#9ecc49]' : 'text-white'
                }`}
              >
                <span>Vi tilbyder</span>
                <span className="text-xs bg-[#3d5229] text-[#9ecc49] px-2 py-0.5 font-normal">Alle ydelser</span>
              </Link>

              <button
                type="button"
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className="p-1 text-[#9ecc49] hover:text-white cursor-pointer"
                aria-label="Åbn eller luk undermenu"
              >
                <i className={`fa-solid ${mobileServicesOpen ? 'fa-chevron-up' : 'fa-chevron-down'} text-sm`} aria-hidden="true" />
              </button>
            </div>

            {mobileServicesOpen && (
              <div className="p-3 space-y-3">
                {servicesMenuConfig?.children.map((group) => {
                  const hasChildren = Array.isArray(group.children) && group.children.length > 0;
                  const isExpanded = mobileExpandedGroup[group.title] ?? true;

                  return (
                    <div key={group.path} className="border border-[#233323] bg-[#141f14] p-2.5">
                      <div className="flex items-center justify-between">
                        <Link
                          to={group.path}
                          className="font-bold text-sm text-[#9ecc49] hover:underline flex items-center gap-1.5"
                        >
                          <i className="fa-solid fa-angle-right text-xs" aria-hidden="true" />
                          <span>{group.title}</span>
                        </Link>

                        {hasChildren && (
                          <button
                            type="button"
                            onClick={() => toggleMobileGroup(group.title)}
                            className="text-xs text-gray-400 hover:text-white px-2 py-1 cursor-pointer"
                          >
                            <i className={`fa-solid ${isExpanded ? 'fa-[#9ecc49] fa-minus' : 'fa-plus'}`} aria-hidden="true" />
                          </button>
                        )}
                      </div>

                      {hasChildren && isExpanded && (
                        <div className="mt-2 pt-2 border-t border-[#233323] pl-3 space-y-1.5">
                          {group.children.map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className={`block text-xs py-1 px-2 rounded-none transition-colors ${
                                location.pathname === subItem.path
                                  ? 'bg-[#3d5229] text-white font-bold'
                                  : 'text-gray-300 hover:text-[#9ecc49] hover:bg-[#233323]'
                              }`}
                            >
                              • {subItem.title}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Autoriseret kloakmester */}
          <NavLink
            to="/ydelser/autoriseret-kloakmester"
            className={({ isActive }) =>
              `block px-4 py-3 rounded-none text-base font-semibold ${
                isActive ? 'bg-[#3d5229] text-[#9ecc49]' : 'text-gray-200 hover:bg-[#233323]'
              }`
            }
          >
            Autoriseret kloakmester
          </NavLink>

          {/* Om os */}
          <NavLink
            to="/om-os"
            className={({ isActive }) =>
              `block px-4 py-3 rounded-none text-base font-semibold ${
                isActive ? 'bg-[#3d5229] text-[#9ecc49]' : 'text-gray-200 hover:bg-[#233323]'
              }`
            }
          >
            Om os
          </NavLink>

          {/* Kontakt */}
          <NavLink
            to="/kontakt"
            className={({ isActive }) =>
              `block px-4 py-3 rounded-none text-base font-semibold ${
                isActive ? 'bg-[#3d5229] text-[#9ecc49]' : 'text-gray-200 hover:bg-[#233323]'
              }`
            }
          >
            Kontakt
          </NavLink>

          {/* Mobile CTA Buttons */}
          <div className="pt-4 border-t border-[#233323] space-y-2">
            <a
              href="tel:22231010"
              className="w-full bg-[#9ecc49] hover:bg-[#8ebb3c] text-[#1f241d] font-bold py-3 px-4 rounded-none flex items-center justify-center gap-2 text-sm whitespace-nowrap"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
              <span className="whitespace-nowrap">Ring Tlf. 22 23 10 10</span>
            </a>
            <Link
              to="/kontakt"
              className="w-full bg-[#3d5229] hover:bg-[#2e3f1e] text-white font-bold py-3 px-4 rounded-none flex items-center justify-center gap-2 text-sm text-center block whitespace-nowrap"
            >
              <span>Få et godt tilbud</span>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
